import React, { useState } from 'react';
import { flowRight } from 'lodash';
import memoizeOne from 'memoize-one';
import { type IHostProps } from '@wix/yoshi-flow-editor';
import { SECTION_MY_POSTS, getLayoutName } from '@wix/communities-blog-client-common';
import AnimatedLoader from '../../../common/components/animated-loader';
import PostListProGallery from '../../../common/components/post-list-pro-gallery';
import { connect } from '../../../common/components/runtime-context';
import {
  POST_ACTION_CHANGE_LANGUAGE,
  POST_ACTION_SHARE,
  POST_ACTION_TRANSLATE,
  POST_ACTIONS,
  type PostAction,
} from '../../../common/constants/post-actions';
import Pagination from '../../../common/containers/pagination/pagination';
import withPermissions, { type WithPermissions } from '../../../common/hoc/with-permissions';
import { getCanSeeMoreButton } from '../../../common/selectors/app-settings-selectors';
import { getLayoutType } from '../../../common/selectors/layout-selectors';
import { getMyPosts } from '../../../common/selectors/post-selectors';
import { getIsUserGuestWriter } from '../../../common/store/auth/current-user-selectors';
import { getIsEntityLoading } from '../../../common/store/is-loading/is-loading-selectors';
import { getPostCount } from '../../../common/store/post-count/post-count-selector';
import {
  MY_POSTS_PAGE_SIZE,
  MY_POSTS_PAGE_TAB_PUBLICATIONS,
  MY_POSTS_PAGE_TAB_DRAFTS,
  MY_POSTS_PAGE_TAB_SUBMISSIONS,
} from '../../constants/my-posts-page';
import { getActiveTab } from '../../store/active-tab/active-tab-selectors';
import { MY_POSTS_ENTITY } from '../../store/initial-data/fetch-initial-data';
import NoPosts from './no-posts';
import styles from './my-posts.scss';

type PostActionsByTab = Record<string, PostAction[]>;

const getVisibleActions = memoizeOne(
  (activeTab: string, isUserGuestWriter: boolean) =>
    ((
      {
        [MY_POSTS_PAGE_TAB_PUBLICATIONS]: isUserGuestWriter
          ? [POST_ACTION_SHARE, POST_ACTION_TRANSLATE, POST_ACTION_CHANGE_LANGUAGE]
          : POST_ACTIONS,
        [MY_POSTS_PAGE_TAB_DRAFTS]: [],
        [MY_POSTS_PAGE_TAB_SUBMISSIONS]: [],
      } as PostActionsByTab
    )[activeTab]),
);
type Props = OwnProps & WithPermissions & ReturnType<typeof mapRuntimeToProps>;

const MyPosts = ({
  posts,
  totalPostCount,
  componentId,
  canSeeMoreButton,
  isLoading,
  layoutType,
  activeTab,
  isUserGuestWriter,
  fetchPageData,
}: Props) => {
  const [page, setPage] = useState(1);

  const handlePageClick = ({ page }: { page: number }) => {
    setPage(page);
    fetchPageData(page);
  };

  return (
    <AnimatedLoader isLoading={isLoading}>
      {posts.length ? (
        <>
          <PostListProGallery
            layoutName={getLayoutName(layoutType)}
            layoutType={layoutType}
            section={SECTION_MY_POSTS}
            domId={componentId}
            allPosts={posts}
            canSeeMoreButton={canSeeMoreButton}
            visibleActions={getVisibleActions(activeTab, isUserGuestWriter)}
            isLoading={isLoading}
          />
          <Pagination
            dataHook="my-posts-pagination-container"
            useDescriptionColor={true}
            page={page}
            onChange={handlePageClick}
            pageSize={MY_POSTS_PAGE_SIZE}
            section={SECTION_MY_POSTS}
            entityCount={totalPostCount}
            createPageUrl={() => ''}
            containerClassName={styles.paginationContainer}
          />
        </>
      ) : (
        <NoPosts />
      )}
    </AnimatedLoader>
  );
};

type OwnProps = {
  canSee: (action: string, entity: string) => boolean;
};

import { type Actions, type AppState } from '../../../common/types';

const mapRuntimeToProps = (
  state: AppState,
  ownProps: OwnProps,
  actions: Actions,
  host: IHostProps,
) => ({
  totalPostCount: getPostCount(state),
  posts: getMyPosts(state),
  componentId: host.id,
  fetchPageData: actions.fetchMyPostsPageDataPromisified,
  canSeeMoreButton: getCanSeeMoreButton(state, ownProps.canSee),
  isLoading: getIsEntityLoading(state, MY_POSTS_ENTITY),
  layoutType: getLayoutType(state, SECTION_MY_POSTS),
  activeTab: getActiveTab(state),
  isUserGuestWriter: getIsUserGuestWriter(state),
});

export default flowRight(withPermissions, connect(mapRuntimeToProps))(MyPosts);
